import type { Lang } from '../types';

export interface ChannelManagerText {
  // Segmented control
  segChannelManager: string;
  segIcalFeeds: string;
  segIcalDisabled: string;

  // Connection state
  notConnectedTitle: string;
  notConnectedDesc: string;
  goToProfile: string;
  connectedTitle: string;
  disabledTitle: string;
  disabledDesc: string;
  readOnlyWarning: string;
  webhookSecretMissing: string;

  // Property mapping
  propertyLabel: string;
  propertyPlaceholder: string;
  loadingProperties: string;
  noProperties: string;
  syncPastReservationsLabel: string;
  syncPastReservationsHint: string;
  syncPastReservationsOnSuccess: string;
  syncPastReservationsOffSuccess: string;
  linkedTo: string;
  syncNow: string;
  changeProperty: string;
  changePropertyHint: string;
  unlink: string;
  linking: string;
  linkSuccess: string;
  unlinkSuccess: string;
  confirmUnlink: string;

  // Linked status card
  statusPillConnected: string;
  statusPillActionNeeded: string;
  statusPillPaused: string;
  linkedHealthySub: string;
  webhookWaitingSub: string;
  webhookExpiredSub: string;
  channelManagerLabel: string;
  metaPastLabel: string;
  metaPastIncluded: string;
  metaPastExcluded: string;

  // Manage settings / danger zone
  manageSettings: string;
  dangerZoneLabel: string;
  unlinkDangerCopy: string;
  unlinkConfirmPrompt: string;
  unlinkConfirmYes: string;

  // Switch confirmation
  confirmTitle: string;
  confirmIntro: (n: number) => string;
  confirmKeepReservations: string;
  confirmKeepForms: string;
  confirmKeepPast: string;
  confirmRemoveFeeds: (feeds: string) => string;
  confirmDiscard: (n: number) => string;
  confirmSwitch: string;
  cancel: string;

  // Triage
  attentionTitle: string;
  attentionEmpty: string;
  attentionResolve: string;
  attentionEdit: string;
  attentionResolved: string;
  attentionRemove: string;
  attentionConfirmRemove: string;
  attentionConfirmBulkRemove: (n: number) => string;
  attentionScopeAll: string;
  attentionScopePast: string;
  attentionScopeIncoming: string;
  attentionStatusPlaceholder: string;
  attentionSetStatus: string;
  attentionMarkResolved: string;
  duplicatesTitle: string;
  duplicatesIntro: string;
  duplicatesKeep: string;
  duplicatesRemove: string;
  duplicatesSwap: string;
  duplicatesReunite: string;
  duplicatesReuniting: string;
  duplicatesConfirm: (keepId: number, removeId: number) => string;
  duplicatesRationaleHasData: string;
  duplicatesRationaleNeither: string;
  duplicatesMerged: string;
  duplicatesNoData: string;
  duplicatesForm: (n: number) => string;
  duplicatesCityTax: string;
  duplicatesUbyport: string;
  duplicatesCheckinLink: string;
  duplicatesPmsCode: string;
  statusPending: string;
  statusConfirmed: string;
  statusCheckedIn: string;
  statusCheckedOut: string;
  statusCancelled: string;

  // Booking.com link tip
  guideBookingTitle: string;
  guideBookingWhy: string;
  guideBookingStep1: string;
  guideBookingStep2: string;
  guideBookingStep3: string;
  guideAirbnbNote: string;

  // Status
  lastEventAt: string;
  lastPollAt: string;
  never: string;

  // Message templates
  messagesTitle: string;
  messagesIntro: string;
  msgFormLink: string;
  msgFormLinkWhen: string;
  msgReminder: string;
  msgReminderWhen: string;
  msgGuideReady: string;
  msgGuideReadyWhen: string;
  msgEnabled: string;
  msgUsingDefault: string;
  /** Shown in place of the body snippet on a collapsed card when the message is switched off. */
  msgDisabledSnippet: string;
  msgPlaceholders: string;
  msgInsert: string;
  msgPreview: string;
  msgHidePreview: string;
  msgPreviewTitle: string;
  msgSave: string;
  msgSaving: string;
  msgSaved: string;
  msgReset: string;
  msgConfirmReset: string;
  msgLinkWarning: string;
  msgTiming: string;
  msgTimingImmediate: string;
  msgTimingBefore: string;
  msgTimingDaysBefore: string;
  msgTimingSameDay: string;
  msgTimingHour: string;
}

export const channelManagerText: Record<Lang, ChannelManagerText> = {
  cs: {
    segChannelManager: 'Channel manager',
    segIcalFeeds: 'iCal kalendáře',
    segIcalDisabled: 'iCal kalendáře — vypnuto',

    notConnectedTitle: 'Hostex není propojen',
    notConnectedDesc: 'Propojte svůj účet Hostex v nastavení profilu. Poté zde přiřadíte apartmán k nemovitosti.',
    goToProfile: 'Přejít do profilu →',
    connectedTitle: 'Hostex je jediný zdroj rezervací pro tento apartmán',
    disabledTitle: 'Propojení s Hostexem je pozastaveno',
    disabledDesc: 'Hostex odmítl uložený token. Propojte účet znovu, aby se rezervace opět synchronizovaly.',
    readOnlyWarning: 'Token je pouze pro čtení. Rezervace se stahují, ale hostům nelze posílat zprávy. Vytvořte v Hostexu token s právem zápisu.',
    webhookSecretMissing: 'Chybí webhook secret. Dokončete nastavení podle návodu níže — bez něj přijímáme události jen na základě tajné adresy.',

    propertyLabel: 'Nemovitost v Hostexu',
    propertyPlaceholder: 'Vyberte nemovitost…',
    loadingProperties: 'Načítám nemovitosti…',
    noProperties: 'Ve vašem účtu Hostex nebyly nalezeny žádné nemovitosti.',
    syncPastReservationsLabel: 'Synchronizovat i minulé rezervace',
    syncPastReservationsHint: 'Zahrne i pobyty, které už skončily, až 180 dní zpětně — to je limit samotného Hostexu.',
    syncPastReservationsOnSuccess: 'Minulé rezervace budou zahrnuty při příští synchronizaci.',
    syncPastReservationsOffSuccess: 'Minulé rezervace budou při příští synchronizaci přeskočeny.',
    linkedTo: 'Propojeno s',
    syncNow: 'Synchronizovat',
    changeProperty: 'Změnit nemovitost',
    changePropertyHint: 'Změna znovu napáruje nadcházející rezervace na novou nemovitost. Stávající formuláře hostů i minulé pobyty zůstanou beze změny.',
    unlink: 'Zrušit propojení',
    linking: 'Propojuji…',
    linkSuccess: 'Apartmán je propojen s Hostexem.',
    unlinkSuccess: 'Propojení zrušeno.',
    confirmUnlink: 'Zrušit propojení s Hostexem? Rezervace, se kterými už host pracoval, zůstanou zachovány.',

    statusPillConnected: 'Připojeno',
    statusPillActionNeeded: 'Vyžaduje pozornost',
    statusPillPaused: 'Pozastaveno',
    linkedHealthySub: 'Rezervace, kontaktní údaje hostů i zprávy se synchronizují automaticky — nic tu nevyžaduje vaši pozornost.',
    webhookWaitingSub: 'Čekáme na potvrzení webhooku od Hostexu — jakmile dorazí první událost, vyřeší se to samo.',
    webhookExpiredSub: 'Webhook nedorazil včas. Události se prozatím přijímají jen na základě tajné adresy.',
    channelManagerLabel: 'Channel manager',
    metaPastLabel: 'Minulé rezervace',
    metaPastIncluded: 'Zahrnuty, 180 dní zpět',
    metaPastExcluded: 'Nezahrnuty',

    manageSettings: 'Spravovat nastavení',
    dangerZoneLabel: 'Nebezpečná zóna',
    unlinkDangerCopy: 'Zrušením propojení se přestanou synchronizovat nové rezervace. Rezervace, se kterými už host pracoval, zůstanou zachovány a označí se k revizi — nic se nemaže.',
    unlinkConfirmPrompt: 'Zrušit propojení s Hostexem?',
    unlinkConfirmYes: 'Ano, zrušit propojení',

    confirmTitle: 'Přepnout na živé propojení s Hostexem?',
    confirmIntro: (n) => `Tento apartmán má ${n === 1 ? '1 iCal kalendář' : n >= 2 && n <= 4 ? `${n} iCal kalendáře` : `${n} iCal kalendářů`}. Hostex doručuje stejné rezervace, proto budou odstraněny.`,
    confirmKeepReservations: 'Stávající rezervace zůstanou a propojí se s Hostexem',
    confirmKeepForms: 'Odkazy na formuláře hostů zůstanou funkční — už vyplněné údaje se neztratí',
    confirmKeepPast: 'Minulé rezervace zůstanou beze změny',
    confirmRemoveFeeds: (feeds) => `Budou odstraněny tyto kalendáře: ${feeds}`,
    confirmDiscard: (n) => `${n} nedotčených budoucích rezervací bude smazáno (znovu se načtou z Hostexu)`,
    confirmSwitch: 'Propojit s Hostexem',
    cancel: 'Zrušit',

    attentionTitle: 'Vyžaduje pozornost',
    attentionEmpty: 'Nic nevyžaduje pozornost.',
    attentionResolve: 'Vyřešeno',
    attentionEdit: 'Upravit',
    attentionResolved: 'Označeno jako vyřešené.',
    attentionRemove: 'Odstranit',
    attentionConfirmRemove: 'Trvale odstranit tuto rezervaci?',
    attentionConfirmBulkRemove: (n) => `Trvale odstranit ${n} rezervac${n === 1 ? 'i' : n >= 2 && n <= 4 ? 'e' : 'í'}?`,
    attentionScopeAll: 'Vše',
    attentionScopePast: 'Minulé',
    attentionScopeIncoming: 'Nadcházející',
    attentionStatusPlaceholder: 'Vyberte stav…',
    attentionSetStatus: 'Nastavit stav',
    attentionMarkResolved: 'Označit jako vyřešené',
    duplicatesTitle: 'Možné duplicity',
    duplicatesIntro: 'Tyto dvojice vypadají jako jeden a tentýž pobyt — stejný příjezd, odjezd i jméno hosta. Sloučením zůstane záznam s údaji hosta a převezme identifikátory od toho druhého.',
    duplicatesRationaleHasData: 'Ponechá záznam, který už obsahuje údaje hosta',
    duplicatesRationaleNeither: 'Ani jeden neobsahuje údaje hosta — ponecháme původní',
    duplicatesKeep: 'Ponechat',
    duplicatesRemove: 'Odstranit',
    duplicatesSwap: 'Prohodit',
    duplicatesReunite: 'Sloučit',
    duplicatesReuniting: 'Slučuji…',
    duplicatesConfirm: (keepId, removeId) =>
      `Sloučit rezervaci #${removeId} do #${keepId}? Rezervace #${removeId} bude trvale odstraněna.`,
    duplicatesMerged: 'Rezervace byly sloučeny.',
    duplicatesNoData: 'žádná data hosta',
    duplicatesForm: (n) => `${n === 1 ? '1 host ve formuláři' : `${n} hostů ve formuláři`}`,
    duplicatesCityTax: 'městský poplatek',
    duplicatesUbyport: 'Ubyport odesláno',
    duplicatesCheckinLink: 'aktivní odkaz',
    duplicatesPmsCode: 'kód Hostexu',
    statusPending: 'Čeká na potvrzení',
    statusConfirmed: 'Potvrzeno',
    statusCheckedIn: 'Ubytován',
    statusCheckedOut: 'Odhlášen',
    statusCancelled: 'Zrušeno',

    guideBookingTitle: 'Nedaří se posílat odkazy přes Booking.com?',
    guideBookingWhy: 'Booking.com ve výchozím nastavení odstraňuje odkazy ze zpráv hostům. Bez tohoto kroku host odkaz na formulář nedostane.',
    guideBookingStep1: 'Otevřete Booking.com Extranet → Account → Messaging settings.',
    guideBookingStep2: 'Najděte nastavení povolených domén (whitelist).',
    guideBookingStep3: 'Přidejte doménu guestbook.cz a uložte.',
    guideAirbnbNote: 'Airbnb odkazy u potvrzených rezervací povoluje, žádné nastavení není potřeba.',

    lastEventAt: 'Poslední událost',
    lastPollAt: 'Poslední kontrola',
    never: 'zatím nikdy',

    messagesTitle: 'Zprávy hostům',
    messagesIntro: 'Zprávy se posílají do konverzace na kanálu, kde host rezervoval (Airbnb, Booking…). Pokud konverzace není k dispozici, použije se e-mail.',
    msgFormLink: 'Odkaz na registrační formulář',
    msgFormLinkWhen: 'Odešle se, jakmile rezervace dorazí z Hostexu',
    msgReminder: 'Připomenutí nevyplněné knihy hostů',
    msgReminderWhen: 'Odešle se v den příjezdu, pokud kniha hostů není kompletní',
    msgGuideReady: 'Průvodce ubytováním je připraven',
    msgGuideReadyWhen: 'Odešle se po vyplnění knihy hostů',
    msgEnabled: 'Posílat tuto zprávu',
    msgDisabledSnippet: 'Vypnuto — hostům se tato zpráva neodešle',
    msgUsingDefault: 'Výchozí text',
    msgPlaceholders: 'Zástupné výrazy',
    msgInsert: 'Vložit',
    msgPreview: 'Náhled',
    msgHidePreview: 'Skrýt náhled',
    msgPreviewTitle: 'Takto zprávu uvidí host',
    msgSave: 'Uložit',
    msgSaving: 'Ukládám…',
    msgSaved: 'Uloženo.',
    msgReset: 'Obnovit výchozí',
    msgConfirmReset: 'Obnovit výchozí text této zprávy?',
    msgLinkWarning: 'Zpráva obsahuje odkaz. Booking.com odkazy odstraňuje, dokud nepovolíte doménu — viz návod výše.',
    msgTiming: 'Odeslat',
    msgTimingImmediate: 'ihned po rezervaci',
    msgTimingBefore: 'před příjezdem',
    msgTimingDaysBefore: 'dní před příjezdem',
    msgTimingSameDay: 'v den příjezdu',
    msgTimingHour: 'Odesílá se v 9:00',
  },

  en: {
    segChannelManager: 'Channel manager',
    segIcalFeeds: 'iCal feeds',
    segIcalDisabled: 'iCal feeds — off',

    notConnectedTitle: 'Hostex is not connected',
    notConnectedDesc: 'Connect your Hostex account in profile settings. You can then map this apartment to a property here.',
    goToProfile: 'Go to profile →',
    connectedTitle: 'Hostex is the only reservation source for this apartment',
    disabledTitle: 'The Hostex connection is paused',
    disabledDesc: 'Hostex rejected the stored token. Reconnect the account to resume syncing.',
    readOnlyWarning: 'This token is read-only. Reservations sync, but guests cannot be messaged. Create a writable token in Hostex.',
    webhookSecretMissing: 'Webhook secret missing. Finish setup using the guide below — until then events are accepted on the secret address alone.',

    propertyLabel: 'Hostex property',
    propertyPlaceholder: 'Choose a property…',
    loadingProperties: 'Loading properties…',
    noProperties: 'No properties found on your Hostex account.',
    syncPastReservationsLabel: 'Also sync past reservations',
    syncPastReservationsHint: 'Includes stays that have already checked out, up to 180 days back — Hostex\'s own limit.',
    syncPastReservationsOnSuccess: 'Past reservations will be included on the next sync.',
    syncPastReservationsOffSuccess: 'Past reservations will be skipped on the next sync.',
    linkedTo: 'Linked to',
    syncNow: 'Sync now',
    changeProperty: 'Change property',
    changePropertyHint: 'Changing this re-matches upcoming reservations to the new property. Existing guest forms and past stays are kept exactly as they are.',
    unlink: 'Unlink',
    linking: 'Linking…',
    linkSuccess: 'Apartment linked to Hostex.',
    unlinkSuccess: 'Unlinked.',
    confirmUnlink: 'Unlink from Hostex? Reservations a guest has already worked on are kept.',

    statusPillConnected: 'Connected',
    statusPillActionNeeded: 'Action needed',
    statusPillPaused: 'Paused',
    linkedHealthySub: 'Reservations, guest details and messages sync automatically — nothing needs your attention here.',
    webhookWaitingSub: 'Waiting for Hostex to confirm the webhook — this resolves itself once the first event arrives.',
    webhookExpiredSub: 'No webhook arrived in time. Events are currently accepted on the secret address alone.',
    channelManagerLabel: 'Channel manager',
    metaPastLabel: 'Past reservations',
    metaPastIncluded: 'Included, 180d back',
    metaPastExcluded: 'Not included',

    manageSettings: 'Manage settings',
    dangerZoneLabel: 'Danger zone',
    unlinkDangerCopy: "Unlinking stops new reservations from syncing. Reservations a guest has already worked on are kept and flagged for review — nothing is deleted.",
    unlinkConfirmPrompt: 'Unlink from Hostex?',
    unlinkConfirmYes: 'Yes, unlink',

    confirmTitle: 'Switch to the Hostex live connection?',
    confirmIntro: (n) => `This apartment has ${n} iCal feed${n === 1 ? '' : 's'}. Hostex already delivers those same bookings, so they will be removed.`,
    confirmKeepReservations: 'Existing reservations are kept and re-linked to Hostex',
    confirmKeepForms: 'Guest form links stay live — anything already filled in is safe',
    confirmKeepPast: 'Past reservations are left exactly as they are',
    confirmRemoveFeeds: (feeds) => `These feeds will be removed: ${feeds}`,
    confirmDiscard: (n) => `${n} untouched future reservation${n === 1 ? '' : 's'} will be deleted (Hostex will re-import them)`,
    confirmSwitch: 'Link to Hostex',
    cancel: 'Cancel',

    attentionTitle: 'Needs attention',
    attentionEmpty: 'Nothing needs attention.',
    attentionResolve: 'Resolve',
    attentionEdit: 'Edit',
    attentionResolved: 'Marked as resolved.',
    attentionRemove: 'Remove',
    attentionConfirmRemove: 'Remove this reservation permanently?',
    attentionConfirmBulkRemove: (n) => `Remove ${n} reservation${n === 1 ? '' : 's'} permanently?`,
    attentionScopeAll: 'All',
    attentionScopePast: 'Past',
    attentionScopeIncoming: 'Incoming',
    attentionStatusPlaceholder: 'Choose status…',
    attentionSetStatus: 'Set status',
    attentionMarkResolved: 'Mark resolved',
    duplicatesTitle: 'Possible duplicates',
    duplicatesIntro: 'These pairs look like one and the same stay — same arrival, same departure, same guest name. Merging keeps the record holding the guest data and moves the identifiers across from the other.',
    duplicatesRationaleHasData: 'Keeps the record that already holds guest data',
    duplicatesRationaleNeither: 'Neither holds guest data — keeping the original',
    duplicatesKeep: 'Keep',
    duplicatesRemove: 'Remove',
    duplicatesSwap: 'Swap',
    duplicatesReunite: 'Merge',
    duplicatesReuniting: 'Merging…',
    duplicatesConfirm: (keepId, removeId) =>
      `Merge reservation #${removeId} into #${keepId}? #${removeId} will be permanently deleted.`,
    duplicatesMerged: 'Reservations merged.',
    duplicatesNoData: 'no guest data',
    duplicatesForm: (n) => `${n} guest${n === 1 ? '' : 's'} on form`,
    duplicatesCityTax: 'city tax',
    duplicatesUbyport: 'sent to Ubyport',
    duplicatesCheckinLink: 'live link',
    duplicatesPmsCode: 'Hostex code',
    statusPending: 'Pending',
    statusConfirmed: 'Confirmed',
    statusCheckedIn: 'Checked in',
    statusCheckedOut: 'Checked out',
    statusCancelled: 'Cancelled',

    guideBookingTitle: "Can't send links on Booking.com?",
    guideBookingWhy: 'Booking.com strips links out of guest messages by default. Without this step, guests never receive the form link.',
    guideBookingStep1: 'Open Booking.com Extranet → Account → Messaging settings.',
    guideBookingStep2: 'Find the allowed domains (whitelist) setting.',
    guideBookingStep3: 'Add the domain guestbook.cz and save.',
    guideAirbnbNote: 'Airbnb allows links on confirmed bookings, so nothing is needed there.',

    lastEventAt: 'Last event',
    lastPollAt: 'Last check',
    never: 'never',

    messagesTitle: 'Guest messages',
    messagesIntro: 'Messages go into the conversation on the channel the guest booked through (Airbnb, Booking…). When no conversation is available, email is used instead.',
    msgFormLink: 'Registration form link',
    msgFormLinkWhen: 'Sent as soon as the reservation arrives from Hostex',
    msgReminder: 'Incomplete guestbook reminder',
    msgReminderWhen: 'Sent on the day of arrival if the guestbook is not finished',
    msgGuideReady: 'Check-in guide is ready',
    msgGuideReadyWhen: 'Sent once the guestbook is complete',
    msgEnabled: 'Send this message',
    msgDisabledSnippet: "Off — guests won't be sent this message",
    msgUsingDefault: 'Default wording',
    msgPlaceholders: 'Placeholders',
    msgInsert: 'Insert',
    msgPreview: 'Preview',
    msgHidePreview: 'Hide preview',
    msgPreviewTitle: 'This is what the guest sees',
    msgSave: 'Save',
    msgSaving: 'Saving…',
    msgSaved: 'Saved.',
    msgReset: 'Restore default',
    msgConfirmReset: 'Restore the default wording for this message?',
    msgLinkWarning: 'This message contains a link. Booking.com strips links until you whitelist the domain — see the guide above.',
    msgTiming: 'Send',
    msgTimingImmediate: 'as soon as the booking arrives',
    msgTimingBefore: 'before arrival',
    msgTimingDaysBefore: 'days before arrival',
    msgTimingSameDay: 'on the day of arrival',
    msgTimingHour: 'Sent at 9:00',
  },

  vi: {
    segChannelManager: 'Channel manager',
    segIcalFeeds: 'Lịch iCal',
    segIcalDisabled: 'Lịch iCal — đã tắt',

    notConnectedTitle: 'Chưa kết nối Hostex',
    notConnectedDesc: 'Hãy kết nối tài khoản Hostex trong cài đặt hồ sơ. Sau đó bạn có thể gán căn hộ này với một bất động sản.',
    goToProfile: 'Tới hồ sơ →',
    connectedTitle: 'Hostex là nguồn đặt phòng duy nhất cho căn hộ này',
    disabledTitle: 'Kết nối Hostex đang tạm dừng',
    disabledDesc: 'Hostex đã từ chối token đã lưu. Hãy kết nối lại để tiếp tục đồng bộ.',
    readOnlyWarning: 'Token này chỉ đọc. Đặt phòng vẫn đồng bộ nhưng không thể nhắn tin cho khách. Hãy tạo token có quyền ghi trong Hostex.',
    webhookSecretMissing: 'Thiếu webhook secret. Hãy hoàn tất theo hướng dẫn bên dưới — hiện tại sự kiện chỉ được chấp nhận dựa trên địa chỉ bí mật.',

    propertyLabel: 'Bất động sản trên Hostex',
    propertyPlaceholder: 'Chọn bất động sản…',
    loadingProperties: 'Đang tải danh sách…',
    noProperties: 'Không tìm thấy bất động sản nào trong tài khoản Hostex.',
    syncPastReservationsLabel: 'Đồng bộ cả các đặt phòng trong quá khứ',
    syncPastReservationsHint: 'Bao gồm cả các kỳ lưu trú đã trả phòng, tối đa 180 ngày trước đây — đây là giới hạn của chính Hostex.',
    syncPastReservationsOnSuccess: 'Các đặt phòng trong quá khứ sẽ được đồng bộ ở lần tiếp theo.',
    syncPastReservationsOffSuccess: 'Các đặt phòng trong quá khứ sẽ bị bỏ qua ở lần đồng bộ tiếp theo.',
    linkedTo: 'Đã liên kết với',
    syncNow: 'Đồng bộ ngay',
    changeProperty: 'Đổi bất động sản',
    changePropertyHint: 'Thay đổi mục này sẽ ghép lại các đặt phòng sắp tới với bất động sản mới. Biểu mẫu khách hiện có và các kỳ lưu trú trong quá khứ vẫn được giữ nguyên.',
    unlink: 'Hủy liên kết',
    linking: 'Đang liên kết…',
    linkSuccess: 'Đã liên kết căn hộ với Hostex.',
    unlinkSuccess: 'Đã hủy liên kết.',
    confirmUnlink: 'Hủy liên kết với Hostex? Các đặt phòng khách đã thao tác sẽ được giữ lại.',

    statusPillConnected: 'Đã kết nối',
    statusPillActionNeeded: 'Cần xử lý',
    statusPillPaused: 'Tạm dừng',
    linkedHealthySub: 'Đặt phòng, thông tin liên hệ của khách và tin nhắn được đồng bộ tự động — không có gì cần bạn xử lý ở đây.',
    webhookWaitingSub: 'Đang chờ Hostex xác nhận webhook — việc này sẽ tự giải quyết khi sự kiện đầu tiên đến.',
    webhookExpiredSub: 'Webhook không đến kịp thời gian. Sự kiện hiện chỉ được chấp nhận dựa trên địa chỉ bí mật.',
    channelManagerLabel: 'Channel manager',
    metaPastLabel: 'Đặt phòng trong quá khứ',
    metaPastIncluded: 'Đã bao gồm, 180 ngày trước',
    metaPastExcluded: 'Không bao gồm',

    manageSettings: 'Quản lý cài đặt',
    dangerZoneLabel: 'Khu vực nguy hiểm',
    unlinkDangerCopy: 'Hủy liên kết sẽ ngừng đồng bộ các đặt phòng mới. Các đặt phòng khách đã thao tác sẽ được giữ lại và đánh dấu để xem lại — không có gì bị xóa.',
    unlinkConfirmPrompt: 'Hủy liên kết với Hostex?',
    unlinkConfirmYes: 'Đồng ý, hủy liên kết',

    confirmTitle: 'Chuyển sang kết nối trực tiếp Hostex?',
    confirmIntro: (n) => `Căn hộ này có ${n} lịch iCal. Hostex đã cung cấp cùng những đặt phòng đó, nên chúng sẽ bị xóa.`,
    confirmKeepReservations: 'Các đặt phòng hiện có được giữ lại và liên kết với Hostex',
    confirmKeepForms: 'Liên kết biểu mẫu của khách vẫn hoạt động — dữ liệu đã điền không bị mất',
    confirmKeepPast: 'Các đặt phòng trong quá khứ giữ nguyên',
    confirmRemoveFeeds: (feeds) => `Những lịch sau sẽ bị xóa: ${feeds}`,
    confirmDiscard: (n) => `${n} đặt phòng tương lai chưa ai thao tác sẽ bị xóa (Hostex sẽ nhập lại)`,
    confirmSwitch: 'Liên kết với Hostex',
    cancel: 'Hủy',

    attentionTitle: 'Cần xem lại',
    attentionEmpty: 'Không có gì cần xem lại.',
    attentionResolve: 'Đã xử lý',
    attentionEdit: 'Sửa',
    attentionResolved: 'Đã đánh dấu là đã xử lý.',
    attentionRemove: 'Xóa',
    attentionConfirmRemove: 'Xóa vĩnh viễn đặt phòng này?',
    attentionConfirmBulkRemove: (n) => `Xóa vĩnh viễn ${n} đặt phòng?`,
    attentionScopeAll: 'Tất cả',
    attentionScopePast: 'Đã qua',
    attentionScopeIncoming: 'Sắp tới',
    attentionStatusPlaceholder: 'Chọn trạng thái…',
    attentionSetStatus: 'Đặt trạng thái',
    attentionMarkResolved: 'Đánh dấu đã xử lý',
    duplicatesTitle: 'Có thể trùng lặp',
    duplicatesIntro: 'Những cặp này có vẻ là cùng một kỳ lưu trú — cùng ngày đến, ngày đi và tên khách. Khi gộp, bản ghi có dữ liệu khách sẽ được giữ lại và nhận mã định danh từ bản còn lại.',
    duplicatesRationaleHasData: 'Giữ lại bản ghi đã có dữ liệu khách',
    duplicatesRationaleNeither: 'Không bản nào có dữ liệu khách — giữ bản gốc',
    duplicatesKeep: 'Giữ lại',
    duplicatesRemove: 'Xóa',
    duplicatesSwap: 'Đổi chiều',
    duplicatesReunite: 'Gộp',
    duplicatesReuniting: 'Đang gộp…',
    duplicatesConfirm: (keepId, removeId) =>
      `Gộp đặt phòng #${removeId} vào #${keepId}? #${removeId} sẽ bị xóa vĩnh viễn.`,
    duplicatesMerged: 'Đã gộp các đặt phòng.',
    duplicatesNoData: 'không có dữ liệu khách',
    duplicatesForm: (n) => `${n} khách trong biểu mẫu`,
    duplicatesCityTax: 'thuế lưu trú',
    duplicatesUbyport: 'đã gửi Ubyport',
    duplicatesCheckinLink: 'liên kết đang hoạt động',
    duplicatesPmsCode: 'mã Hostex',
    statusPending: 'Chờ xử lý',
    statusConfirmed: 'Đã xác nhận',
    statusCheckedIn: 'Đã nhận phòng',
    statusCheckedOut: 'Đã trả phòng',
    statusCancelled: 'Đã hủy',

    guideBookingTitle: 'Không gửi được liên kết trên Booking.com?',
    guideBookingWhy: 'Booking.com mặc định loại bỏ liên kết trong tin nhắn gửi khách. Nếu bỏ qua bước này, khách sẽ không nhận được liên kết biểu mẫu.',
    guideBookingStep1: 'Mở Booking.com Extranet → Account → Messaging settings.',
    guideBookingStep2: 'Tìm mục danh sách miền được phép (whitelist).',
    guideBookingStep3: 'Thêm miền guestbook.cz và lưu lại.',
    guideAirbnbNote: 'Airbnb cho phép liên kết với các đặt phòng đã xác nhận, không cần thiết lập gì thêm.',

    lastEventAt: 'Sự kiện gần nhất',
    lastPollAt: 'Lần kiểm tra gần nhất',
    never: 'chưa bao giờ',

    messagesTitle: 'Tin nhắn cho khách',
    messagesIntro: 'Tin nhắn được gửi vào cuộc trò chuyện trên kênh mà khách đã đặt (Airbnb, Booking…). Nếu không có cuộc trò chuyện, hệ thống sẽ dùng email.',
    msgFormLink: 'Liên kết biểu mẫu đăng ký',
    msgFormLinkWhen: 'Gửi ngay khi đặt phòng đến từ Hostex',
    msgReminder: 'Nhắc hoàn tất sổ đăng ký khách',
    msgReminderWhen: 'Gửi vào ngày nhận phòng nếu sổ đăng ký chưa hoàn tất',
    msgGuideReady: 'Hướng dẫn nhận phòng đã sẵn sàng',
    msgGuideReadyWhen: 'Gửi sau khi sổ đăng ký được hoàn tất',
    msgEnabled: 'Gửi tin nhắn này',
    msgDisabledSnippet: 'Đang tắt — khách sẽ không nhận được tin nhắn này',
    msgUsingDefault: 'Nội dung mặc định',
    msgPlaceholders: 'Trường thay thế',
    msgInsert: 'Chèn',
    msgPreview: 'Xem trước',
    msgHidePreview: 'Ẩn xem trước',
    msgPreviewTitle: 'Đây là những gì khách nhìn thấy',
    msgSave: 'Lưu',
    msgSaving: 'Đang lưu…',
    msgSaved: 'Đã lưu.',
    msgReset: 'Khôi phục mặc định',
    msgConfirmReset: 'Khôi phục nội dung mặc định cho tin nhắn này?',
    msgLinkWarning: 'Tin nhắn này có chứa liên kết. Booking.com sẽ loại bỏ liên kết cho đến khi bạn thêm miền vào danh sách cho phép — xem hướng dẫn ở trên.',
    msgTiming: 'Gửi',
    msgTimingImmediate: 'ngay khi có đặt phòng',
    msgTimingBefore: 'trước khi đến',
    msgTimingDaysBefore: 'ngày trước khi đến',
    msgTimingSameDay: 'vào ngày đến',
    msgTimingHour: 'Gửi lúc 9:00',
  },

  uk: {
    segChannelManager: 'Channel manager',
    segIcalFeeds: 'Календарі iCal',
    segIcalDisabled: 'Календарі iCal — вимкнено',

    notConnectedTitle: 'Hostex не підключено',
    notConnectedDesc: 'Підключіть обліковий запис Hostex у налаштуваннях профілю. Після цього тут можна прив’язати апартаменти до об’єкта.',
    goToProfile: 'Перейти до профілю →',
    connectedTitle: 'Hostex — єдине джерело бронювань для цих апартаментів',
    disabledTitle: 'Підключення до Hostex призупинено',
    disabledDesc: 'Hostex відхилив збережений токен. Підключіть обліковий запис знову, щоб відновити синхронізацію.',
    readOnlyWarning: 'Токен лише для читання. Бронювання синхронізуються, але надсилати повідомлення гостям не можна. Створіть у Hostex токен із правом запису.',
    webhookSecretMissing: 'Бракує webhook secret. Завершіть налаштування за інструкцією нижче — доки що події приймаються лише за таємною адресою.',

    propertyLabel: 'Об’єкт у Hostex',
    propertyPlaceholder: 'Оберіть об’єкт…',
    loadingProperties: 'Завантаження об’єктів…',
    noProperties: 'У вашому обліковому записі Hostex не знайдено об’єктів.',
    syncPastReservationsLabel: 'Синхронізувати також минулі бронювання',
    syncPastReservationsHint: 'Включає перебування, які вже завершилися, до 180 днів тому — це обмеження самого Hostex.',
    syncPastReservationsOnSuccess: 'Минулі бронювання буде включено під час наступної синхронізації.',
    syncPastReservationsOffSuccess: 'Минулі бронювання буде пропущено під час наступної синхронізації.',
    linkedTo: 'Прив’язано до',
    syncNow: 'Синхронізувати',
    changeProperty: 'Змінити об’єкт',
    changePropertyHint: 'Ця зміна повторно зіставить майбутні бронювання з новим об’єктом. Наявні анкети гостей і минулі перебування залишаться без змін.',
    unlink: 'Відв’язати',
    linking: 'Прив’язую…',
    linkSuccess: 'Апартаменти прив’язано до Hostex.',
    unlinkSuccess: 'Відв’язано.',
    confirmUnlink: 'Відв’язати від Hostex? Бронювання, з якими гість уже працював, буде збережено.',

    statusPillConnected: 'Підключено',
    statusPillActionNeeded: 'Потребує уваги',
    statusPillPaused: 'Призупинено',
    linkedHealthySub: 'Бронювання, контактні дані гостей і повідомлення синхронізуються автоматично — тут нічого не потребує вашої уваги.',
    webhookWaitingSub: 'Очікуємо підтвердження webhook від Hostex — щойно надійде перша подія, це вирішиться само.',
    webhookExpiredSub: 'Webhook не надійшов вчасно. Наразі події приймаються лише за таємною адресою.',
    channelManagerLabel: 'Channel manager',
    metaPastLabel: 'Минулі бронювання',
    metaPastIncluded: 'Включено, 180 днів тому',
    metaPastExcluded: 'Не включено',

    manageSettings: 'Керувати налаштуваннями',
    dangerZoneLabel: 'Небезпечна зона',
    unlinkDangerCopy: 'Відв’язання зупинить синхронізацію нових бронювань. Бронювання, з якими гість уже працював, буде збережено і позначено для перевірки — нічого не видаляється.',
    unlinkConfirmPrompt: 'Відв’язати від Hostex?',
    unlinkConfirmYes: 'Так, відв’язати',

    confirmTitle: 'Перейти на пряме підключення Hostex?',
    confirmIntro: (n) => `Ці апартаменти мають ${n} календар(ів) iCal. Hostex уже передає ті самі бронювання, тому їх буде видалено.`,
    confirmKeepReservations: 'Наявні бронювання зберігаються і прив’язуються до Hostex',
    confirmKeepForms: 'Посилання на анкети гостей залишаються робочими — заповнені дані не втрачаються',
    confirmKeepPast: 'Минулі бронювання залишаються без змін',
    confirmRemoveFeeds: (feeds) => `Буде видалено такі календарі: ${feeds}`,
    confirmDiscard: (n) => `${n} незачеплених майбутніх бронювань буде видалено (Hostex завантажить їх знову)`,
    confirmSwitch: 'Прив’язати до Hostex',
    cancel: 'Скасувати',

    attentionTitle: 'Потребує уваги',
    attentionEmpty: 'Нічого не потребує уваги.',
    attentionResolve: 'Вирішено',
    attentionEdit: 'Редагувати',
    attentionResolved: 'Позначено як вирішене.',
    attentionRemove: 'Видалити',
    attentionConfirmRemove: 'Остаточно видалити це бронювання?',
    attentionConfirmBulkRemove: (n) => `Остаточно видалити ${n} бронювань?`,
    attentionScopeAll: 'Усі',
    attentionScopePast: 'Минулі',
    attentionScopeIncoming: 'Майбутні',
    attentionStatusPlaceholder: 'Оберіть статус…',
    attentionSetStatus: 'Встановити статус',
    attentionMarkResolved: 'Позначити як вирішене',
    duplicatesTitle: 'Можливі дублікати',
    duplicatesIntro: 'Ці пари виглядають як одне й те саме перебування — той самий заїзд, виїзд і ім’я гостя. Об’єднання залишає запис із даними гостя та переносить до нього ідентифікатори з іншого.',
    duplicatesRationaleHasData: 'Залишає запис, який уже містить дані гостя',
    duplicatesRationaleNeither: 'Жоден не містить даних гостя — залишаємо оригінал',
    duplicatesKeep: 'Залишити',
    duplicatesRemove: 'Видалити',
    duplicatesSwap: 'Поміняти',
    duplicatesReunite: 'Об’єднати',
    duplicatesReuniting: 'Об’єдную…',
    duplicatesConfirm: (keepId, removeId) =>
      `Об’єднати бронювання #${removeId} з #${keepId}? #${removeId} буде остаточно видалено.`,
    duplicatesMerged: 'Бронювання об’єднано.',
    duplicatesNoData: 'немає даних гостя',
    duplicatesForm: (n) => `${n} гост${n === 1 ? 'ь' : 'ей'} у формі`,
    duplicatesCityTax: 'міський податок',
    duplicatesUbyport: 'надіслано в Ubyport',
    duplicatesCheckinLink: 'активне посилання',
    duplicatesPmsCode: 'код Hostex',
    statusPending: 'Очікує',
    statusConfirmed: 'Підтверджено',
    statusCheckedIn: 'Заселено',
    statusCheckedOut: 'Виселено',
    statusCancelled: 'Скасовано',

    guideBookingTitle: 'Не вдається надіслати посилання через Booking.com?',
    guideBookingWhy: 'Booking.com за замовчуванням вирізає посилання з повідомлень гостям. Без цього кроку гість не отримає посилання на анкету.',
    guideBookingStep1: 'Відкрийте Booking.com Extranet → Account → Messaging settings.',
    guideBookingStep2: 'Знайдіть налаштування дозволених доменів (whitelist).',
    guideBookingStep3: 'Додайте домен guestbook.cz і збережіть.',
    guideAirbnbNote: 'Airbnb дозволяє посилання для підтверджених бронювань, там нічого налаштовувати не треба.',

    lastEventAt: 'Остання подія',
    lastPollAt: 'Остання перевірка',
    never: 'ще ніколи',

    messagesTitle: 'Повідомлення гостям',
    messagesIntro: 'Повідомлення надсилаються у розмову на каналі, через який гість забронював (Airbnb, Booking…). Якщо розмови немає, використовується електронна пошта.',
    msgFormLink: 'Посилання на анкету реєстрації',
    msgFormLinkWhen: 'Надсилається щойно бронювання надходить із Hostex',
    msgReminder: 'Нагадування про незаповнену книгу гостей',
    msgReminderWhen: 'Надсилається у день заїзду, якщо книгу гостей не заповнено',
    msgGuideReady: 'Інструкція для заселення готова',
    msgGuideReadyWhen: 'Надсилається після заповнення книги гостей',
    msgEnabled: 'Надсилати це повідомлення',
    msgDisabledSnippet: 'Вимкнено — гості не отримають це повідомлення',
    msgUsingDefault: 'Стандартний текст',
    msgPlaceholders: 'Підстановки',
    msgInsert: 'Вставити',
    msgPreview: 'Попередній перегляд',
    msgHidePreview: 'Сховати перегляд',
    msgPreviewTitle: 'Ось що побачить гість',
    msgSave: 'Зберегти',
    msgSaving: 'Зберігаю…',
    msgSaved: 'Збережено.',
    msgReset: 'Відновити стандартний',
    msgConfirmReset: 'Відновити стандартний текст цього повідомлення?',
    msgLinkWarning: 'Повідомлення містить посилання. Booking.com вирізає посилання, доки ви не додасте домен до дозволених — див. інструкцію вище.',
    msgTiming: 'Надіслати',
    msgTimingImmediate: 'щойно надійде бронювання',
    msgTimingBefore: 'перед заїздом',
    msgTimingDaysBefore: 'днів перед заїздом',
    msgTimingSameDay: 'у день заїзду',
    msgTimingHour: 'Надсилається о 9:00',
  },
};
